import useCallApi from '../../utilities/axios'

export type AgendasQuery = {
    search?: string
    createdDate?: string
    pageNumber?: number
    pageSize?: number
}

export type ApproveAgendaRequest = {
    isApprove: boolean
    signUserID?: string
    signUserPinCode?: string
    remark?: string
}

export const useSettingAgendas = () => {
    const callApi = useCallApi()

    const getAgendas = async (irb: string, params: AgendasQuery = {}) => {
        const base = `${process.env.REACT_APP_CU_IRB_API_URL}/agendas/${irb}`
        const qs = new URLSearchParams()
        if (params.search?.trim()) qs.set('search', params.search.trim())
        if (params.createdDate) qs.set('createdDate', params.createdDate)
        if (params.pageNumber != null) qs.set('pageNumber', String(params.pageNumber))
        if (params.pageSize != null) qs.set('pageSize', String(params.pageSize))
        const url = qs.toString() ? `${base}?${qs.toString()}` : base
        const { data } = await callApi.axiosGet({ url })
        return data
    }

    const getAgendaById = async (irb: string, agendaId: string) => {
        const { data } = await callApi.axiosGet<any>({
            url: `${process.env.REACT_APP_CU_IRB_API_URL}/agendas/${irb}/${agendaId}`,
        })
        return data
    }

    const upsertAgendas = async (irb: string, values: any) => {
        const { data } = await callApi.axiosPut<any>({
            url: `${process.env.REACT_APP_CU_IRB_API_URL}/agendas/${irb}`,
            params: values,
        })
        return data
    }

    const approveAgenda = async (irb: string, agendaId: string, values: ApproveAgendaRequest) => {
        const { data } = await callApi.axiosPost<any>({
            url: `${process.env.REACT_APP_CU_IRB_API_URL}/agendas/approve/${irb}/${agendaId}`,
            params: values,
        })
        return data
    }

    const sendMail = async (irb: string, agendaId: string) => {
        const { data } = await callApi.axiosPut<any>({
            url: `${process.env.REACT_APP_CU_IRB_API_URL}/agendas/sendMail/${irb}/${agendaId}`,
            params: undefined,
        })
        return data
    }

    const getAgendaFolderById = async (irb: string, agendaId: string) => {
        const { data } = await callApi.axiosGet<any>({
            url: `${process.env.REACT_APP_CU_IRB_API_URL}/agendas/folder/${irb}/${agendaId}`,
        })
        return data
    }

    const deleteAgenda = async (irb: string, agendaId: string) => {
        const { data } = await callApi.axiosDelete<any>({
            url: `${process.env.REACT_APP_CU_IRB_API_URL}/agendas/${irb}/${agendaId}`,
        })
        return data
    }

    const getAgendaReportById = async (irb: string, agendaId: string, agendaReportId?: string) => {
        const base = `${process.env.REACT_APP_CU_IRB_API_URL}/agendas/report/${irb}/${agendaId}`
        const url = agendaReportId ? `${base}/${agendaReportId}` : base
        const { data } = await callApi.axiosGet<any>({ url })
        return data
    }

    const upsertAgendaReport = async (irb: string, values: any) => {
        const { data } = await callApi.axiosPut<any>({
            url: `${process.env.REACT_APP_CU_IRB_API_URL}/agendas/report/${irb}`,
            params: values,
        })
        return data
    }

    const approveAgendaReport = async (
        irb: string,
        agendaId: string,
        agendaReportId: string,
        values: ApproveAgendaRequest,
    ) => {
        const { data } = await callApi.axiosPost<any>({
            url: `${process.env.REACT_APP_CU_IRB_API_URL}/agendas/report/approve/${irb}/${agendaId}/${agendaReportId}`,
            params: values,
        })
        return data
    }

    const sendMailReport = async (irb: string, agendaId: string, agendaReportId: string) => {
        const { data } = await callApi.axiosPut<any>({
            url: `${process.env.REACT_APP_CU_IRB_API_URL}/agendas/sendMail/${irb}/${agendaId}/${agendaReportId}`,
            params: undefined,
        })
        return data
    }

    const getExportAgendaById = async (irb: string, agendaId: string, token: string) => {
        const { data } = await callApi.axiosGet<any>({
            url: `${process.env.REACT_APP_CU_IRB_API_URL}/agendas/export/${irb}/${agendaId}`,
            token,
        })
        return data
    }

    const getAgendaFile = async (irb: string, agendaId: string) => {
        const { data } = await callApi.axiosGet<any>({
            url: `${process.env.REACT_APP_CU_IRB_API_URL}/agendas/file/${irb}/${agendaId}`,
        })
        return data
    }

    const getAgendaReportFile = async (irb: string, agendaId: string, agendaReportId: string) => {
        const { data } = await callApi.axiosGet<any>({
            url: `${process.env.REACT_APP_CU_IRB_API_URL}/agendas/file/${irb}/${agendaId}/${agendaReportId}`,
        })
        return data
    }

    const getAgendaFileArrayBuffer = async (irb: string, agendaId: string, key: string, fileName: string) => {
        const { data } = await callApi.axiosGet<any>({
            url: `${process.env.REACT_APP_CU_IRB_API_URL}/agendas/file-download/${irb}/${agendaId}/${key}?filename=${fileName}`,
            config: {
                responseType: 'arraybuffer',
            },
        })
        return data
    }

    const getAgendaReportFileArrayBuffer = async (
        irb: string,
        agendaId: string,
        agendaReportId: string,
        type: string,
        key: string,
        fileName: string,
    ) => {
        const { data } = await callApi.axiosGet<any>({
            url: `${process.env.REACT_APP_CU_IRB_API_URL}/agendas/file-download/report/${irb}/${agendaId}/${agendaReportId}/${type}/${key}?filename=${fileName}`,
            config: {
                responseType: 'arraybuffer',
            },
        })
        return data
    }

    return {
        getAgendas,
        getAgendaById,
        upsertAgendas,
        approveAgenda,
        sendMail,
        getAgendaFolderById,
        deleteAgenda,
        getAgendaReportById,
        upsertAgendaReport,
        approveAgendaReport,
        sendMailReport,
        getExportAgendaById,
        getAgendaFile,
        getAgendaReportFile,
        getAgendaFileArrayBuffer,
        getAgendaReportFileArrayBuffer,
    }
}
